import { Col, message, Radio, Row, Table } from "antd";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { IconRight } from "../../../assets/icons";
import moment from "moment";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import api from "../../../api/api";
import { getFormattedNumber } from "@utils";
import { PRIVATE_ROUTE } from "../../../route.constants";
import { ADMIN_ROLE } from "../../../constants";
import ReportDetails from "./ReportDetails/ReportDetails";
import OrderDetails from "../../OrderManagement/OrderDetails/OrderDetails";
import { useTranslation } from "react-i18next";

const RootContainer = styled.div`
  > .ant-row .ant-col {
    width: 20%;
  }

  .ant-radio-group {
    display: flex;
    gap: 8px;
    width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .ant-radio-button-wrapper {
    width: 25%;
    padding: 8px 12px;
    height: 48px;
    border-radius: 8px;
    border-inline-start-width: 1px;
    border: 1px solid ${(props) => props.theme.primaryColor};
    background-color: ${(props) => props.theme.backgroundColor};
    color: #1d1014;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    white-space: nowrap;

    ::before {
      display: none;
    }
  }

  .ant-radio-button-wrapper-checked {
    background-color: ${(props) => props.theme.primaryColor};
    color: #fff;

    :hover {
      color: #fff;
    }
  }

  .ant-table svg {
    cursor: pointer;
  }

  .ant-picker {
    width: 100%;
    height: 48px;
  }
`;

const Title = styled.div`
  padding-top: 16px;
  padding-bottom: 24px;
  font-size: 24px;
  font-weight: 700;
  color: #1e1e1e;
`;

const Card = styled.div`
  padding: 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  justify-content: center;
  background: ${(props) => props.theme.backgroundColor};
`;

const Text = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #1d1014;
`;

const BoldText = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1d1014;
`;

const { HOME, REPORTS, ADMIN_REPORT } = PRIVATE_ROUTE;

const AdminReport = () => {
  const navigate = useNavigate();
  const { role } = useParams();
  const [loading, setLoading] = useState(true);
  const [countLoading, setCountLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [data, setData] = useState([]);

  const {t} = useTranslation();

  useEffect(() => {
    getAdminCounts();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getAdmins(role);

    // eslint-disable-next-line
  }, [role]);

  const getAdminCounts = () => {
    api
      .getAdminCounts()
      .then(({ data }) => {
        setCountLoading(false);
        setCounts(data);
      })
      .catch((error) => {
        message.error(error);
      });
  };

  const getAdmins = (role) => {
    setLoading(true);
    setData([]);
    api
      .getAdmins({
        role,
      })
      .then(({ data }) => {
        setLoading(false);
        setData(data);
      })
      .catch((error) => {
        setLoading(false);
        message.error(error);
      });
  };

  const goToDetails = (item) => {
    navigate(`details/${item.id}?name=${item.name}&username=${item.username}`);
  };

  const handleStatusChange = (e) => {
    navigate(`/${HOME}/${REPORTS}/${ADMIN_REPORT}/${e.target.value}`, {
      replace: true,
    });
  };

  const dynamicColumns =
    role === "ORDER_ADMIN"
      ? [
          {
            key: "actions",
            render: (_, record) => (
              <IconRight onClick={() => goToDetails(record)} />
            ),
          },
        ]
      : [];

  const columns = [
    {
      key: "key",
      title: t('no.'),
      render: (_, record, index) => index + 1,
    },
    {
      key: "name",
      dataIndex: "name",
      title: t('admin'),
    },
    {
      key: "username",
      dataIndex: "username",
      title: t('admin_id'),
    },
    {
      key: "created_at",
      dataIndex: "created_at",
      title: t('joined_date'),
      render: (_, record) =>
        moment(record.created_at).format("DD MMM YYYY, HH:mm"),
    },
    ...dynamicColumns,
  ];

  return (
    <RootContainer>
      <Title>{t('admin_report')}</Title>
      <Row gutter={[12, 12]}>
        <Col xs={12} md={8} lg={4}>
          <Card>
            <Text>{t('total_admin')}</Text>
            <BoldText>
              {countLoading ? "..." : getFormattedNumber(counts.total_admins)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card>
            <Text>{t('super_admin')}</Text>
            <BoldText>
              {countLoading ? "..." : getFormattedNumber(counts.super_admins)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card>
            <Text>{t('template_admin')}</Text>
            <BoldText>
              {countLoading
                ? "..."
                : getFormattedNumber(counts.template_admins)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card>
            <Text>{t('order_admin')}</Text>
            <BoldText>
              {countLoading ? "..." : getFormattedNumber(counts.order_admins)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={12} md={8} lg={4}>
          <Card>
            <Text>{t('data_entry_admin')}</Text>
            <BoldText>
              {countLoading
                ? "..."
                : getFormattedNumber(counts.data_entry_admins)}
            </BoldText>
          </Card>
        </Col>
        <Col xs={24}>
          <Radio.Group onChange={handleStatusChange} value={role}>
            <Row gutter={[12, 12]} style={{width: '100%'}}>
              {Object.keys(ADMIN_ROLE).map((key) => (
                <Col xs={12} md={6} key={key}>
                  <Radio.Button style={{width: '100%'}} value={key}>
                    {ADMIN_ROLE[key] === "Super Admin" ? t('super_admin') :
                    ADMIN_ROLE[key] === "Template Admin" ? t('template_admin') :
                    ADMIN_ROLE[key] === "Order Admin" ? t('order_admin') :
                    t('data_entry_admin')
                    }
                  </Radio.Button>
                </Col>
              ))}
            </Row>
          </Radio.Group>
        </Col>
        <Col xs={24}>
          <Table
            dataSource={data}
            columns={columns}
            loading={loading}
            pagination={false}
            scroll={{x : 900}}
          />
        </Col>
      </Row>
    </RootContainer>
  );
};

const Entry = () => {
  return (
    <Routes>
      <Route path="" element={<AdminReport />} />
      <Route path="/details/:admin_id" element={<ReportDetails />} />
      <Route path="/details/:admin_id/:id" element={<OrderDetails />} />
    </Routes>
  );
};

export default Entry;
